import { useEffect, useRef } from "react"

import NumberFlow from "@number-flow/react"
import { App, Flex, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Avatar from "../../../../../../../Components/Avatar"
import Select from "../../../../../../../Components/Select"
import useAnimatedNumber from "../../../../../../../hooks/useAnimatedNumber"
import { selectDetailAction } from "../../../../../redux/disputes.slice"
import { assignDispute, getTeamMembers } from "../../../../../redux/disputes.thunks"

import groupStyles from "../../group.module.less"
import styles from "./heroCard.module.less"

const { Text } = Typography

// statusInfo.tone is a data value; map it to this module's dot colour class.
const TONE_CLASS = {
  success: "toneSuccess",
  danger: "toneDanger",
  info: "toneInfo",
  warning: "toneWarning",
  neutral: "toneNeutral"
}

// The bordered strip above the field groups: the disputed amount and the status read
// as one block, with the dispute's single assignee control set aside at the right edge.
const HeroCard = ({ dispute, disputeId, statusInfo, onAssigneeChange }) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const dispatch = useDispatch()

  const teamMembers = useSelector((s) => s.disputes.team.members)
  const members = Array.isArray(teamMembers) ? teamMembers : teamMembers?.results || []
  const currentAssignee = dispute?.assigned_to || null

  useEffect(() => {
    if (!members.length) dispatch(getTeamMembers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The hero shows the ORIGINAL disputed amount in its own currency (e.g. 35.10 GBP),
  // not the USD-settled value; falls back to the settled amount when there's no
  // original (US charges are 1:1 anyway).
  const animatedAmount = useAnimatedNumber(Number(dispute?.original_amount ?? dispute?.amount) || 0)

  // Assign failures: the list slice rolls back, but this card reads a local copy of the
  // dispute, so revert its optimistic assignee to `prev` on a failed assign for this
  // dispute.
  const detailAction = useSelector(selectDetailAction)
  const assignResultRef = useRef(null)
  useEffect(() => {
    const res = detailAction?.lastResult
    if (!res || res === assignResultRef.current || res.verb !== "assign") return
    assignResultRef.current = res
    if (!res.ok && String(res.id) === String(disputeId)) onAssigneeChange(res.prev ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailAction?.lastResult])

  // Assigning optimistically updates the page's local detail copy and dispatches the
  // assign action, which asks the backend to email the new assignee.
  const handleAssign = async (memberId) => {
    const member = memberId ? members.find((m) => String(m.id) === String(memberId)) : null
    const lite = member ? { id: member.id, name: member.name, email: member.email } : null
    onAssigneeChange(lite)
    try {
      // `emailed` is false while the assign endpoint is still unwired on this backend —
      // the card keeps the optimistic assignee, we just don't promise an email that
      // never went out.
      const { emailed } = await dispatch(
        assignDispute({ id: disputeId, member: lite, prev: currentAssignee })
      ).unwrap()
      if (!lite) {
        notification.info({ message: t("Assignment cleared.") })
        return
      }
      notification.success({
        message: emailed
          ? t("Assigned to _name_. We emailed them the dispute.", { name: lite.name })
          : t("Assigned to _name_.", { name: lite.name })
      })
    } catch {
      /* error handled in thunk */
    }
  }

  // Initials avatar beside the name in both the closed control and the dropdown
  // options, so an owner reads at a glance (no profile photos).
  const renderMember = (opt) => (
    <Flex align="center" gap={8}>
      <Avatar name={opt.label} size={22} />
      <span>{opt.label}</span>
    </Flex>
  )

  return (
    <div className={styles.hero}>
      <div className={styles.cell}>
        <div className={groupStyles.groupLabel}>{t("Disputed transaction value")}</div>
        <div className={styles.amount}>
          <NumberFlow
            value={animatedAmount}
            format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <span className={styles.amountCcy}>{dispute.original_currency || dispute.currency}</span>
        </div>
      </div>
      <div className={cls(styles.cell, styles.cellStatus)}>
        <div className={groupStyles.groupLabel}>{t("Status")}</div>
        <div className={cls(styles.statusRow, styles[TONE_CLASS[statusInfo.tone]])}>
          <span className={styles.dot} />
          <div className={styles.statusText}>
            <div className={styles.statusTitle}>{statusInfo.title}</div>
            <Text type="secondary" className={styles.statusSub}>
              {statusInfo.sub}
            </Text>
          </div>
        </div>
      </div>
      {/* Set aside at the edge of the card so it doesn't compete with the amount and
          status. Picking a member assigns + emails them. Editable in EVERY status,
          settled disputes included. */}
      <div className={cls(styles.cell, styles.cellAssignee)}>
        <div className={groupStyles.groupLabel}>{t("Assignee")}</div>
        {/* No allowClear / onClear: a dispute must always have an owner, so this
            reassigns to another member but can never be emptied. */}
        <Select
          value={currentAssignee ? currentAssignee.id : undefined}
          placeholder={t("Assign to a team member")}
          options={members.map((m) => ({ value: m.id, label: m.name }))}
          onChange={handleAssign}
          notFoundContent={t("No team members yet")}
          optionRender={renderMember}
          labelRender={renderMember}
          getPopupContainer={() => document.getElementById("main-layout") || document.body}
          className={cls(styles.assigneeSelect, styles.assigneeSelectRow)}
        />
      </div>
    </div>
  )
}

export default HeroCard
